/**
 * @fileoverview The "Main Door" of our Nexme app
 * 
 * Think of this file like the main entrance to a building. When someone visits
 * our website, this is the first file that runs. It's like a receptionist who
 * sets up everything needed for the visitor - shows them where to go, gives them
 * the right keys, and makes sure all the services are working.
 * 
 * This file tells the browser: "Hey, here's our entire app, and here's how
 * to organize everything so it works properly!"
 * 
 * @author Nexme Development Team
 * @version 1.0.0
 */

// The basic building blocks - like getting the tools from your toolbox
import React from "react";                    // The main React "engine" - like the motor of a car
import ReactDOM from "react-dom/client";     // The tool that puts our app on the webpage

// The "memory system" - like a filing cabinet that remembers everything
import { Provider } from "react-redux";      // The manager of our filing cabinet
import { store } from "store";               // The actual filing cabinet with all our data

// The "GPS system" - helps users navigate between different pages
import { BrowserRouter } from "react-router-dom";

// Our main app - like the actual house we're building
import App from "App";

// The "paint and decorations" - makes everything look pretty
import "theme/index.scss";                   // Our color scheme and styling
import "./index.css";                        // Basic styling rules

// The "translator" - makes our app speak different languages
import "utils/i18n";

// The "helpers" - each one handles a specific job
import UserAuth from "context/authProvider";        // The security guard - checks who you are
import UserRoute from "context/routeProvider";      // The tour guide - shows you around
import ErrorBoundary from "ErrorBoundry";           // The safety net - catches problems
import { TenantProvider } from "context/tenantProvider"; // The landlord - manages different "apartments"
import ModalProvider from "context/modal";          // The popup manager - handles popup windows
import SocketProvider from "context/socket";        // The phone line - for real-time chat

// The "data manager" - fetches and stores information from the internet
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools"; // Debugging tools

// The "login system" - lets users sign in with Google
import { GoogleOAuthProvider } from "@react-oauth/google";

// The "loading screen" - what users see while the app is starting up
import ClientLayout from "components/splashScreen";

/**
 * Creates the "foundation" where our app will live
 * 
 * Think of this like finding the perfect spot in your yard to build a house.
 * We're telling React: "Hey, put our entire app inside this specific spot
 * on the webpage (the element with id='root')"
 * 
 * @type {ReactDOM.Root}
 */
const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

/**
 * Builds our entire app like stacking Russian dolls!
 * 
 * Imagine you have a set of Russian nesting dolls. Each doll has a specific job,
 * and they all fit inside each other. The biggest doll (outermost) handles the
 * most general things, and the smallest doll (innermost) is our actual app.
 * 
 * Here's how our "dolls" are stacked (from outside to inside):
 * 
 * 1. React.StrictMode - The "quality checker" - makes sure everything works perfectly
 * 2. GoogleOAuthProvider - The "Google login helper" - lets users sign in with Google
 * 3. QueryClientProvider - The "internet data manager" - fetches and stores data from servers
 * 4. ErrorBoundary - The "safety net" - catches any crashes and shows a friendly error message
 * 5. SocketProvider - The "phone line" - enables real-time chat and live updates
 * 6. TenantProvider - The "apartment manager" - handles different versions of our app
 * 7. UserRoute - The "tour guide" - decides which pages users can see
 * 8. BrowserRouter - The "GPS system" - handles moving between different pages
 * 9. Redux Provider - The "memory bank" - stores all the app's important information
 * 10. UserAuth - The "security guard" - checks if users are logged in
 * 11. ModalProvider - The "popup manager" - handles popup windows and dialogs
 * 12. ClientLayout - The "loading screen" - shows while the app is starting up
 * 13. App - Our actual application - the main content users see
 * 
 * @description Each "doll" provides services to all the "dolls" inside it,
 * like a family where everyone helps each other!
 */
root.render(
  <React.StrictMode>
    <GoogleOAuthProvider
      clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID as string}
    >
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary fallback={<p>Something went wrong</p>}>
          <SocketProvider>
            <TenantProvider>
              <UserRoute>
                <BrowserRouter>
                  <Provider store={store}>
                    <UserAuth>
                      <ModalProvider>
                        <ClientLayout>
                          <App />
                        </ClientLayout>
                      </ModalProvider>
                    </UserAuth>
                  </Provider>
                </BrowserRouter>
              </UserRoute>
            </TenantProvider>
          </SocketProvider>
        </ErrorBoundary>
        {/* The "debugging microscope" - only shows up when developers are working */}
        <ReactQueryDevtools
          initialIsOpen={false}
          buttonPosition="bottom-left"
        />
      </QueryClientProvider>
    </GoogleOAuthProvider>
  </React.StrictMode>
);

/**
 * The "Offline Assistant" Setup
 * 
 * Think of a service worker like having a helpful assistant who works even when
 * you're not around. This assistant can:
 * - Remember things for you (caching) - like keeping your favorite snacks in the fridge
 * - Work in the background - like doing laundry while you sleep
 * - Send you notifications - like a personal reminder system
 * - Make your app faster - by keeping important things ready to use
 * 
 * This code is like hiring this assistant. First, we check if we already have one
 * (we don't want to hire two assistants!). If not, we hire a new one from our
 * "service-worker.js" file.
 * 
 * @description It's like having a smart house that remembers your preferences
 * and keeps everything running smoothly, even when you're not actively using it!
 */
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    // Check if we already have an assistant working for us
    const isRegistered = registrations.some(
      (registration) =>
        registration.active?.scriptURL.includes("/service-worker.js") &&
        registration.active?.state === "activated"
    );

    if (!isRegistered) {
      // Hire a new assistant if we don't have one yet
      navigator.serviceWorker
        .register("/service-worker.js")
        .then((registration) => {
          console.log(
            "Service Worker registered with scope:",
            registration.scope
          );
        })
        .catch((error) => {
          console.error("Service Worker registration failed:", error);
        });
    } else {
      console.log("Service Worker already registered.");
    }
  });
}
